import { useEffect, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { showError } from '@utils';
import { vehiclesServices } from '@services';

import {
  filtersModalConditionVehiclesChange,
  filtersModalRulesVehiclesChange,
  resetModalVehiclesChange,
  selectVehiclesState,
} from 'store/catalog/vehiclesSlice';
import { QUERY_KEYS } from 'constants/queryKeys';
import {
  handleVehicles,
  handleEnums,
  getCatalogVehiclesQueryParamsAndPayload,
} from './utils';
import { CATALOG_CARS_PAGE_SIZE } from './constants';

export const useVehiclesOperations = (fetchOnMount) => {
  const dispatch = useDispatch();

  const { query, isReady } = useRouter();

  const { filters: modalFilters } = useSelector(selectVehiclesState);

  const {
    page = 1,
    pageSize = CATALOG_CARS_PAGE_SIZE,
    orderBy = '',
    order = '',
    token,
    query: searchQuery,
    ...others
  } = query;

  const { cleanedParams, payload } = useMemo(
    () =>
      getCatalogVehiclesQueryParamsAndPayload({
        page,
        pageSize,
        orderBy,
        order,
        query: searchQuery,
        others,
        modalFilters,
      }),
    [page, pageSize, orderBy, order, searchQuery, others, modalFilters]
  );

  const {
    data: vehiclesData,
    isLoading: isLoadingVehicles,
    isError: isErrorVehicles,
    error: errorVehicles,
    refetch: getVehiclesData,
  } = useQuery(
    [
      QUERY_KEYS.GET_CATALOG_CAR_VEHICLES_KEY,
      {
        page,
        pageSize,
        orderBy,
        order,
        query: searchQuery,
        others,
        ...modalFilters?.modalFiltersRules,
      },
    ],
    () => {
      return vehiclesServices.getVehiclesFilters({
        params: cleanedParams,
        payload,
      });
    },
    { refetchOnMount: true, enabled: fetchOnMount && isReady }
  );

  const vehicles = useMemo(
    () => handleVehicles(vehiclesData?.data),
    [vehiclesData]
  );

  const total = vehiclesData?.meta?.total;

  const { data: enumsData } = useQuery([QUERY_KEYS.GET_ENUMS_KEY], () =>
    vehiclesServices?.getVehiclesEnums()
  );

  const enums = useMemo(() => handleEnums(enumsData?.data), [enumsData]);

  const resetModalChange = useCallback(() => {
    dispatch(resetModalVehiclesChange());
  }, [dispatch]);

  const handleFilterModalChange = useCallback(
    (conditions) => {
      dispatch(filtersModalConditionVehiclesChange(conditions));
    },
    [dispatch]
  );
  const handleFiltersChange = useCallback(
    (rules) => {
      dispatch(filtersModalRulesVehiclesChange(rules));
    },
    [dispatch]
  );

  useEffect(() => {
    if (isErrorVehicles) {
      showError(errorVehicles);
    }
  }, [isErrorVehicles, errorVehicles]);

  return {
    page,
    pageSize: parseInt(pageSize, 10) || CATALOG_CARS_PAGE_SIZE,
    total,
    loading: isLoadingVehicles,
    enums,
    vehicles,
    hasErrors: isErrorVehicles,
    errorMessage: errorVehicles,
    filters: modalFilters,
    catalogVehiclesQueryParams: cleanedParams,
    catalogVehiclesPayload: payload,
    getVehicles: getVehiclesData,
    resetModalChange,
    handleFilterModalChange,
    handleFiltersChange,
  };
};
